import { useState, useEffect, useRef, useCallback } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import Navigation from "../components/Navigation";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Tabs, TabsList, TabsTrigger } from "../components/ui/tabs";
import {
  Brain, Sparkles, Crown, Play, Search, X, Dumbbell, Video,
  BookOpen, Eye, Target, TrendingUp, Crosshair, Shield,
  MessageSquare, Users, HeartPulse, Flame, Trophy, Volume2,
  Moon, Zap, Film, Headphones
} from "lucide-react";
import FavoriteButton from "../components/FavoriteButton";
import VideoLibrary from "./VideoLibrary";
import axios from "axios";
import { toast } from "sonner";
import videosByCategory from "../data/videos.json";

// Extracted components & hooks
import { useSpeech } from "../hooks/useSpeech";
import { useGuidedPractice } from "../hooks/useGuidedPractice";
import { StreakTracker } from "../components/training/StreakTracker";
import { FavoritesSection } from "../components/training/FavoritesSection";
import { ExerciseCard } from "../components/training/ExerciseCard";
import { ExerciseDialog } from "../components/training/ExerciseDialog";
import { VideoPlayerModal } from "../components/training/VideoPlayerModal";
import AudioLibrary from "../components/AudioLibrary";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const CATEGORY_META = {
  'all': { label: 'All', icon: Film, activeBg: 'bg-gray-900 text-white', bg: 'bg-gray-50 border-gray-200 text-gray-700' },
  'pre-game': { label: 'Pre-Game', icon: BookOpen, activeBg: 'bg-teal-600 text-white', bg: 'bg-teal-50 border-teal-200 text-teal-700' },
  'visualization': { label: 'Visualization', icon: Eye, activeBg: 'bg-violet-600 text-white', bg: 'bg-violet-50 border-violet-200 text-violet-700' },
  'breathing': { label: 'Breathing', icon: Volume2, activeBg: 'bg-sky-600 text-white', bg: 'bg-sky-50 border-sky-200 text-sky-700' },
  'performance-anxiety': { label: 'Performance Anxiety', icon: Zap, activeBg: 'bg-yellow-600 text-white', bg: 'bg-yellow-50 border-yellow-200 text-yellow-700' },
  'confidence': { label: 'Confidence', icon: Trophy, activeBg: 'bg-amber-600 text-white', bg: 'bg-amber-50 border-amber-200 text-amber-700' },
  'self-talk': { label: 'Self-Talk', icon: MessageSquare, activeBg: 'bg-blue-600 text-white', bg: 'bg-blue-50 border-blue-200 text-blue-700' },
  'goal-setting': { label: 'Goal Setting', icon: Target, activeBg: 'bg-emerald-600 text-white', bg: 'bg-emerald-50 border-emerald-200 text-emerald-700' },
  'emotional-awareness': { label: 'Emotional Awareness', icon: HeartPulse, activeBg: 'bg-pink-600 text-white', bg: 'bg-pink-50 border-pink-200 text-pink-700' },
  'mistake-management': { label: 'Mistake Mgmt', icon: Shield, activeBg: 'bg-slate-600 text-white', bg: 'bg-slate-50 border-slate-200 text-slate-700' },
  'resilience': { label: 'Resilience', icon: Shield, activeBg: 'bg-rose-600 text-white', bg: 'bg-rose-50 border-rose-200 text-rose-700' },
  'leadership': { label: 'Leadership', icon: Crown, activeBg: 'bg-purple-600 text-white', bg: 'bg-purple-50 border-purple-200 text-purple-700' },
  'team-cohesion': { label: 'Team Cohesion', icon: Users, activeBg: 'bg-green-600 text-white', bg: 'bg-green-50 border-green-200 text-green-700' },
  'team-dynamics': { label: 'Team Dynamics', icon: Users, activeBg: 'bg-indigo-600 text-white', bg: 'bg-indigo-50 border-indigo-200 text-indigo-700' },
  'focus': { label: 'Focus', icon: Crosshair, activeBg: 'bg-cyan-600 text-white', bg: 'bg-cyan-50 border-cyan-200 text-cyan-700' },
  'stress-management': { label: 'Stress Mgmt', icon: Moon, activeBg: 'bg-indigo-500 text-white', bg: 'bg-indigo-50 border-indigo-200 text-indigo-700' },
  'arousal-regulation': { label: 'Energy Mgmt', icon: Flame, activeBg: 'bg-orange-600 text-white', bg: 'bg-orange-50 border-orange-200 text-orange-700' },
  'performance-enhancement': { label: 'Performance', icon: TrendingUp, activeBg: 'bg-lime-600 text-white', bg: 'bg-lime-50 border-lime-200 text-lime-700' },
  'motivation': { label: 'Motivation', icon: Sparkles, activeBg: 'bg-fuchsia-600 text-white', bg: 'bg-fuchsia-50 border-fuchsia-200 text-fuchsia-700' },
  'recovery': { label: 'Recovery', icon: HeartPulse, activeBg: 'bg-red-600 text-white', bg: 'bg-red-50 border-red-200 text-red-700' },
  'reframing': { label: 'Reframing', icon: Brain, activeBg: 'bg-teal-500 text-white', bg: 'bg-teal-50 border-teal-200 text-teal-700' },
};

const CATEGORIES = Object.keys(CATEGORY_META);

// Aliases for Videos tab category filter
const CATEGORY_ORDER = CATEGORIES;
const CATEGORY_ICONS = Object.fromEntries(
  Object.entries(CATEGORY_META).map(([key, val]) => [key, val.icon])
);

export default function MentalTraining({ user, onLogout }) {
  const [exercises, setExercises] = useState([]);
  const [sessions, setSessions] = useState([]);
  const [selectedCategory, setSelectedCategory] = useState("all");
  const [selectedExercise, setSelectedExercise] = useState(null);
  const [previousExerciseSessions, setPreviousExerciseSessions] = useState([]);
  const [showDialog, setShowDialog] = useState(false);
  const [showNotesDialog, setShowNotesDialog] = useState(false);
  const [showVideoPlayer, setShowVideoPlayer] = useState(false);
  const [selectedVideo, setSelectedVideo] = useState(null);
  const [currentNotes, setCurrentNotes] = useState("");
  const [exerciseNotes, setExerciseNotes] = useState({});
  const [searchQuery, setSearchQuery] = useState("");
  const [streak, setStreak] = useState(null);
  const [showMilestoneToast, setShowMilestoneToast] = useState(null);
  const [favoriteExercises, setFavoriteExercises] = useState([]);
  const [mainTab, setMainTab] = useState("exercises");
  const [recommendations, setRecommendations] = useState([]);
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const exerciseRefs = useRef({});

  // Hooks
  const speech = useSpeech();
  const guided = useGuidedPractice({
    speakText: speech.speakText,
    stopSpeech: speech.stopSpeech,
    speechCancelledRef: speech.speechCancelledRef,
    getPreferredVoice: speech.getPreferredVoice
  });

  // Auto-advance guided practice steps
  useEffect(() => {
    if (!guided.guidedMode || guided.guidedPaused || guided.guidedComplete || guided.guidedTimer > 0) return;
    if (selectedExercise?.instructions) {
      const isComplete = guided.advanceStep(selectedExercise.instructions);
      if (isComplete) handleCompleteSession();
    }
  }, [guided.guidedTimer, guided.guidedMode, guided.guidedPaused, guided.guidedComplete]);

  // Data fetching
  const fetchExercises = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API}/training/exercises`, { headers: { Authorization: `Bearer ${token}` } });
      setExercises(response.data);
    } catch { toast.error("Failed to load exercises"); }
  };

  const fetchSessions = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API}/training/sessions`, { headers: { Authorization: `Bearer ${token}` } });
      setSessions(response.data);
    } catch { setSessions([]); }
  };

  const fetchStreak = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API}/training/streak`, { headers: { Authorization: `Bearer ${token}` } });
      setStreak(response.data);
    } catch (err) { console.error(err.message); }
  };

  const fetchFavoriteExercises = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API}/favorites`, { headers: { Authorization: `Bearer ${token}` } });
      setFavoriteExercises(response.data.filter(f => f.item_type === 'exercise'));
    } catch (err) { console.error(err.message); }
  };

  const handleFavoriteToggle = useCallback(() => { fetchFavoriteExercises(); }, []);

  // Wellness-based recommendations
  const WELLNESS_TO_CATEGORIES = {
    confidence: ['confidence', 'self-talk', 'visualization'],
    focus: ['focus', 'breathing', 'pre-game'],
    mood: ['emotional-awareness', 'motivation', 'resilience'],
    energy: ['arousal-regulation', 'breathing', 'recovery'],
    stress: ['stress-management', 'breathing', 'emotional-awareness'],
    sleep_quality: ['recovery', 'breathing', 'stress-management'],
    performance_rating: ['performance-enhancement', 'goal-setting', 'visualization'],
  };

  const fetchRecommendations = async () => {
    try {
      const token = localStorage.getItem('token');
      const res = await axios.get(`${API}/wellness/trends/individual?days=7`, { headers: { Authorization: `Bearer ${token}` } });
      const trends = res.data || [];
      if (trends.length === 0 || exercises.length === 0) return;

      // Average recent check-in scores
      const avg = (key) => trends.reduce((s, d) => s + (d[key] || 0), 0) / trends.length;
      const scores = {
        confidence: avg('confidence'),
        focus: avg('focus'),
        mood: avg('mood'),
        energy: avg('energy'),
        stress: avg('stress'), // high stress = bad, so we invert
        sleep_quality: avg('sleep_quality'),
        performance_rating: avg('performance_rating'),
      };

      // Find the 3 weakest areas (for stress, high = weak)
      const ranked = Object.entries(scores)
        .map(([key, val]) => ({ key, score: key === 'stress' ? (10 - val) : val }))
        .sort((a, b) => a.score - b.score)
        .slice(0, 3);

      // Map to exercise categories and pick top exercises
      const seen = new Set();
      const recs = [];
      for (const area of ranked) {
        const cats = WELLNESS_TO_CATEGORIES[area.key] || [];
        for (const cat of cats) {
          const matching = exercises.filter(e => e.category === cat && !seen.has(e.id));
          if (matching.length > 0) {
            const pick = matching[Math.floor(Math.random() * matching.length)];
            seen.add(pick.id);
            recs.push({ exercise: pick, reason: area.key, score: area.score });
            if (recs.length >= 4) break;
          }
        }
        if (recs.length >= 4) break;
      }
      setRecommendations(recs);
    } catch (err) { console.error(err.message); }
  };

  useEffect(() => {
    if (exercises.length > 0) fetchRecommendations();
  }, [exercises]);

  const fetchAllNotes = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API}/training/exercises/notes/all`, { headers: { Authorization: `Bearer ${token}` } });
      const notesMap = {};
      response.data.forEach(note => { notesMap[note.exercise_id] = note.notes; });
      setExerciseNotes(notesMap);
    } catch (err) { console.error(err.message); }
  };

  const handleOpenNotes = async (exercise) => {
    setSelectedExercise(exercise);
    setCurrentNotes(exerciseNotes[exercise.id] || "");
    setShowNotesDialog(true);
  };

  const handleSaveNotes = async () => {
    if (!selectedExercise) return;
    try {
      const token = localStorage.getItem('token');
      await axios.post(`${API}/training/exercises/notes`, {
        exercise_id: selectedExercise.id, exercise_title: selectedExercise.title, notes: currentNotes
      }, { headers: { Authorization: `Bearer ${token}` } });
      setExerciseNotes(prev => ({ ...prev, [selectedExercise.id]: currentNotes }));
      toast.success("Notes saved!");
      setShowNotesDialog(false);
      setCurrentNotes("");
    } catch { toast.error("Failed to save notes"); }
  };

  const handleDeleteNotes = async () => {
    if (!selectedExercise) return;
    try {
      const token = localStorage.getItem('token');
      await axios.delete(`${API}/training/exercises/${selectedExercise.id}/notes`, { headers: { Authorization: `Bearer ${token}` } });
      setExerciseNotes(prev => { const n = { ...prev }; delete n[selectedExercise.id]; return n; });
      toast.success("Notes deleted");
      setShowNotesDialog(false);
      setCurrentNotes("");
    } catch { toast.error("Failed to delete notes"); }
  };

  const fetchPreviousExerciseSessions = async (exerciseId) => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API}/training/sessions?exercise_id=${exerciseId}`, { headers: { Authorization: `Bearer ${token}` } });
      setPreviousExerciseSessions(response.data);
    } catch { setPreviousExerciseSessions([]); }
  };

  const handleCompleteSession = async () => {
    if (!selectedExercise) return;
    try {
      const token = localStorage.getItem('token');
      await axios.post(`${API}/training/sessions`, {
        exercise_id: selectedExercise.id, exercise_title: selectedExercise.title,
        notes: exerciseNotes[selectedExercise.id] || "", duration_minutes: selectedExercise.duration_minutes
      }, { headers: { Authorization: `Bearer ${token}` } });
      toast.success("Session completed!");
      fetchSessions();
      const oldStreak = streak?.current_streak || 0;
      const streakRes = await axios.get(`${API}/training/streak`, { headers: { Authorization: `Bearer ${token}` } });
      const newStreak = streakRes.data;
      setStreak(newStreak);
      const milestones = [3, 7, 14, 30];
      const crossedMilestone = milestones.find(m => oldStreak < m && newStreak.current_streak >= m);
      if (crossedMilestone) {
        const labels = { 3: "3-Day Spark", 7: "7-Day Flow", 14: "14-Day Warrior", 30: "30-Day Legend" };
        setShowMilestoneToast({ days: crossedMilestone, label: labels[crossedMilestone] });
        setTimeout(() => setShowMilestoneToast(null), 5000);
      }
    } catch { toast.error("Failed to complete session"); }
  };

  useEffect(() => {
    fetchExercises();
    fetchSessions();
    fetchAllNotes();
    fetchStreak();
    fetchFavoriteExercises();
  }, []);

  // Refresh recommendations when a daily check-in completes (from any page)
  useEffect(() => {
    const handleCheckin = () => {
      if (exercises.length > 0) fetchRecommendations();
    };
    window.addEventListener('checkin-complete', handleCheckin);
    return () => window.removeEventListener('checkin-complete', handleCheckin);
  }, [exercises]);

  // URL search param handling - auto-select exercises tab for exercise links
  useEffect(() => {
    if (exercises.length === 0) return;

    const exerciseId = searchParams.get('exerciseId');
    const exerciseName = searchParams.get('exercise');
    const categoryParam = searchParams.get('category');
    const searchParam = searchParams.get('search');

    // If URL has exercise params, switch to exercises tab
    if (exerciseId || exerciseName || categoryParam) {
      setMainTab("exercises");
    }

    // If URL has search param, switch to videos tab
    if (searchParam && !exerciseId && !exerciseName) {
      setMainTab("videos");
    }

    // Handle ?exerciseId=...
    if (exerciseId) {
      const exercise = exercises.find(e => e.id === exerciseId);
      if (exercise) {
        setSelectedExercise(exercise);
        fetchPreviousExerciseSessions(exercise.id);
        setShowDialog(true);
        setTimeout(() => { exerciseRefs.current[exerciseId]?.scrollIntoView({ behavior: 'smooth', block: 'center' }); }, 300);
        return;
      }
    }

    // Handle ?exercise=Name (from training plan links)
    if (exerciseName) {
      const query = exerciseName.toLowerCase();
      const keywords = query.split(/[\s-]+/).filter(k => k.length > 2);

      let bestMatch = null;
      let bestScore = 0;
      for (const ex of exercises) {
        let score = 0;
        const title = (ex.title || '').toLowerCase();
        const cat = (ex.category || '').replace(/-/g, ' ').toLowerCase();
        const desc = (ex.description || '').toLowerCase();
        if (title === query) { bestMatch = ex; bestScore = 100; break; }
        for (const k of keywords) {
          if (title.includes(k)) score += 3;
          if (cat.includes(k)) score += 2;
          if (desc.includes(k)) score += 1;
        }
        if (score > bestScore) { bestScore = score; bestMatch = ex; }
      }

      if (bestMatch && bestScore >= 3) {
        setSelectedCategory(bestMatch.category);
        setSelectedExercise(bestMatch);
        fetchPreviousExerciseSessions(bestMatch.id);
        setShowDialog(true);
      } else {
        setSearchQuery(exerciseName);
      }
    }

    // Handle ?category=...
    if (categoryParam) {
      setSelectedCategory(categoryParam);
    }
  }, [searchParams, exercises]);

  const handleSelectExercise = (exercise) => {
    setSelectedExercise(exercise);
    fetchPreviousExerciseSessions(exercise.id);
    setShowDialog(true);
  };

  // Filtering
  const filteredExercises = exercises
    .filter(ex => selectedCategory === "all" || ex.category === selectedCategory)
    .filter(ex => {
      if (!searchQuery.trim()) return true;
      const q = searchQuery.toLowerCase();
      const keywords = q.split(/[\s-]+/).filter(k => k.length > 2);
      const searchable = [
        ex.title, ex.description, ex.category?.replace(/-/g, ' '), ex.type,
        ...(ex.instructions || [])
      ].filter(Boolean).join(' ').toLowerCase();
      return keywords.length > 0
        ? keywords.some(k => searchable.includes(k))
        : searchable.includes(q);
    });

  const shouldShowVideos = selectedCategory !== "all";
  const categoryVideos = shouldShowVideos ? (videosByCategory[selectedCategory] || []) : [];

  return (
    <div className="min-h-screen bg-gray-50 dark:bg-gray-950">
      <Navigation user={user} onLogout={onLogout} />

      <div className="max-w-7xl mx-auto px-3 sm:px-6 lg:px-8 py-4 sm:py-8">
        {/* Header */}
        <div className="mb-4 sm:mb-6">
          <h1 data-testid="mental-training-title" className="text-2xl sm:text-3xl lg:text-4xl font-bold text-gray-900 tracking-tight">
            Mental Training
          </h1>
          <p className="text-sm text-gray-500 mt-1">
            Exercises, guided videos, and sport psychology resources
          </p>
        </div>

        {/* Main Tabs - Exercises vs Videos vs Audio */}
        <div className="mb-6">
          <div className="bg-gradient-to-r from-emerald-600 to-teal-600 rounded-2xl p-1 max-w-lg shadow-lg shadow-emerald-200/50">
            <div className="grid grid-cols-3 gap-1">
              <button
                data-testid="tab-exercises"
                onClick={() => setMainTab("exercises")}
                className={`flex items-center justify-center gap-2 py-3 px-3 rounded-xl text-sm font-bold transition-all duration-200 ${
                  mainTab === "exercises"
                    ? "bg-white text-emerald-700 shadow-md"
                    : "bg-transparent text-white/90 hover:bg-white/15"
                }`}
              >
                <Dumbbell className="w-5 h-5" />
                Exercises
              </button>
              <button
                data-testid="tab-videos"
                onClick={() => setMainTab("videos")}
                className={`flex items-center justify-center gap-2 py-3 px-3 rounded-xl text-sm font-bold transition-all duration-200 ${
                  mainTab === "videos"
                    ? "bg-white text-teal-700 shadow-md"
                    : "bg-transparent text-white/90 hover:bg-white/15"
                }`}
              >
                <Video className="w-5 h-5" />
                Videos
              </button>
              <button
                data-testid="tab-audio"
                onClick={() => setMainTab("audio")}
                className={`flex items-center justify-center gap-2 py-3 px-3 rounded-xl text-sm font-bold transition-all duration-200 ${
                  mainTab === "audio"
                    ? "bg-white text-indigo-700 shadow-md"
                    : "bg-transparent text-white/90 hover:bg-white/15"
                }`}
              >
                <Headphones className="w-5 h-5" />
                Audio
              </button>
            </div>
          </div>
        </div>

        {/* ─── EXERCISES TAB ─── */}
        {mainTab === "exercises" && (
          <>
            <StreakTracker streak={streak} />

            {/* Milestone Toast */}
            {showMilestoneToast && (
              <div data-testid="milestone-celebration" className="fixed top-20 left-1/2 -translate-x-1/2 z-50 animate-bounce">
                <div className="bg-gradient-to-r from-amber-500 to-orange-500 text-white px-6 py-3 rounded-2xl shadow-xl shadow-amber-500/30 flex items-center gap-3">
                  <Crown className="w-6 h-6" />
                  <div>
                    <p className="font-bold text-sm">New Milestone!</p>
                    <p className="text-xs text-amber-100">{showMilestoneToast.label} unlocked</p>
                  </div>
                </div>
              </div>
            )}

            {/* Recommended For You */}
            {recommendations.length > 0 && (
              <div className="mb-8" data-testid="recommended-section">
                <div className="flex items-center gap-2 mb-4">
                  <Sparkles className="w-5 h-5 text-amber-500" />
                  <h2 className="text-lg font-bold text-gray-900">Recommended for You</h2>
                  <span className="text-xs text-gray-400 ml-1">Based on your recent check-ins</span>
                </div>
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3">
                  {recommendations.map(({ exercise, reason, score }) => {
                    const reasonLabels = {
                      confidence: 'Boost Confidence', focus: 'Sharpen Focus', mood: 'Elevate Mood',
                      energy: 'Restore Energy', stress: 'Reduce Stress', sleep_quality: 'Improve Sleep',
                      performance_rating: 'Enhance Performance'
                    };
                    const meta = CATEGORY_META[exercise.category] || CATEGORY_META['all'];
                    const Icon = meta.icon;
                    return (
                      <button
                        key={exercise.id}
                        data-testid={`rec-${exercise.id}`}
                        onClick={() => handleSelectExercise(exercise)}
                        className="text-left p-4 rounded-xl bg-gradient-to-br from-amber-50 to-orange-50 border border-amber-200/60 hover:border-amber-300 hover:shadow-md transition-all duration-200 group"
                      >
                        <div className="flex items-center gap-2 mb-2">
                          <div className={`p-1.5 rounded-lg ${meta.activeBg}`}>
                            <Icon className="w-3.5 h-3.5" />
                          </div>
                          <span className="text-[10px] font-semibold text-amber-700 bg-amber-100 px-2 py-0.5 rounded-full">
                            {reasonLabels[reason] || reason}
                          </span>
                        </div>
                        <h3 className="text-sm font-semibold text-gray-900 line-clamp-1 group-hover:text-amber-800 transition-colors">{exercise.title}</h3>
                        <p className="text-xs text-gray-500 line-clamp-2 mt-1">{exercise.description}</p>
                        <div className="flex items-center gap-1.5 mt-2 text-[10px] text-gray-400">
                          <Dumbbell className="w-3 h-3" />
                          <span>{exercise.duration_minutes || 5} min</span>
                          <span className="mx-1">·</span>
                          <span className="capitalize">{exercise.category?.replace(/-/g, ' ')}</span>
                        </div>
                      </button>
                    );
                  })}
                </div>
              </div>
            )}

            <FavoritesSection
              favoriteExercises={favoriteExercises}
              exercises={exercises}
              onSelectExercise={handleSelectExercise}
            />

            {/* Search */}
            <div className="mb-6">
              <div className="relative max-w-xl">
                <Search className="absolute left-4 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-300" />
                <input
                  data-testid="exercise-search-input"
                  type="text"
                  placeholder="Search exercises by title, description, or keywords..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className="w-full pl-11 pr-10 py-3 text-sm bg-white border border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-emerald-500/40 focus:border-emerald-400 transition-all placeholder:text-gray-300"
                />
                {searchQuery && (
                  <button onClick={() => setSearchQuery("")} className="absolute right-3.5 top-1/2 -translate-y-1/2 text-gray-300 hover:text-gray-500 transition-colors">
                    <X className="w-4 h-4" />
                  </button>
                )}
              </div>
              {searchQuery && <p className="mt-2 text-sm text-gray-500">Found {filteredExercises.length} result{filteredExercises.length !== 1 ? 's' : ''} for &ldquo;{searchQuery}&rdquo;</p>}
            </div>

            {/* Category Filter with Icons */}
            <div className="mb-8">
              <div className="flex flex-wrap gap-2">
                {CATEGORIES.map(cat => {
                  const meta = CATEGORY_META[cat];
                  const Icon = meta.icon;
                  const isActive = selectedCategory === cat;
                  return (
                    <button
                      key={cat}
                      data-testid={`category-${cat}`}
                      onClick={() => setSelectedCategory(cat)}
                      className={`inline-flex items-center gap-1.5 px-3.5 py-2 rounded-lg text-xs font-semibold border transition-all duration-200 ${
                        isActive
                          ? `${meta.activeBg} border-transparent shadow-sm`
                          : `${meta.bg} border hover:shadow-sm`
                      }`}
                    >
                      <Icon className="w-3.5 h-3.5" />
                      {meta.label}
                    </button>
                  );
                })}
              </div>
            </div>

            {/* Exercises - Grouped by Skill Area, sorted by Difficulty */}
            {selectedCategory === "all" && !searchQuery ? (
              // Grouped view: each skill area is a section
              <>
                {Object.entries(CATEGORY_META)
                  .filter(([key]) => key !== "all")
                  .map(([cat, meta]) => {
                    const catExercises = exercises.filter(e => e.category === cat);
                    if (catExercises.length === 0) return null;
                    const Icon = meta.icon;
                    const DIFF_ORDER = { beginner: 0, intermediate: 1, advanced: 2 };
                    const sorted = [...catExercises].sort((a, b) => (DIFF_ORDER[a.difficulty] || 0) - (DIFF_ORDER[b.difficulty] || 0));
                    return (
                      <div key={cat} className="mb-8">
                        <div className="flex items-center space-x-2 mb-3">
                          <div className={`w-8 h-8 rounded-lg flex items-center justify-center ${meta.activeBg}`}>
                            <Icon className="w-4 h-4" />
                          </div>
                          <h3 className="font-bold text-gray-900 dark:text-white text-base">{meta.label}</h3>
                          <span className="text-xs text-gray-400">{catExercises.length} exercises</span>
                        </div>
                        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-3">
                          {sorted.map((exercise) => (
                            <ExerciseCard
                              key={exercise.id}
                              exercise={exercise}
                              exerciseNotes={exerciseNotes}
                              exerciseRef={el => exerciseRefs.current[exercise.id] = el}
                              onSelect={handleSelectExercise}
                              onOpenNotes={handleOpenNotes}
                              onFavoriteToggle={handleFavoriteToggle}
                            />
                          ))}
                        </div>
                      </div>
                    );
                  })
                  .filter(Boolean)}
              </>
            ) : (
              // Filtered/search view: flat grid sorted by difficulty
              <>
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4 mb-8">
                  {[...filteredExercises]
                    .sort((a, b) => {
                      const DIFF_ORDER = { beginner: 0, intermediate: 1, advanced: 2 };
                      return (DIFF_ORDER[a.difficulty] || 0) - (DIFF_ORDER[b.difficulty] || 0);
                    })
                    .map((exercise) => (
                      <ExerciseCard
                        key={exercise.id}
                        exercise={exercise}
                        exerciseNotes={exerciseNotes}
                        exerciseRef={el => exerciseRefs.current[exercise.id] = el}
                        onSelect={handleSelectExercise}
                        onOpenNotes={handleOpenNotes}
                        onFavoriteToggle={handleFavoriteToggle}
                      />
                    ))}
                </div>

                {filteredExercises.length === 0 && (
                  <div className="text-center py-20">
                    <Dumbbell className="w-12 h-12 text-gray-200 mx-auto mb-3" />
                    <p className="text-gray-400 text-sm font-medium">
                      {searchQuery ? `No exercises found for "${searchQuery}"` : "No exercises in this category"}
                    </p>
                  </div>
                )}
              </>
            )}

            {/* Recent Sessions */}
            {sessions.length > 0 && (
              <div className="mb-8">
                <h2 className="text-lg font-bold text-gray-900 mb-4">Recent Sessions</h2>
                <div className="space-y-2 max-w-3xl">
                  {sessions.slice(0, 5).map((session) => (
                    <div key={session.id} className="flex items-center gap-4 p-3.5 bg-white rounded-xl border border-gray-100 hover:border-gray-200 hover:shadow-md transition-all duration-200">
                      <div className="p-2.5 rounded-lg bg-gradient-to-br from-emerald-500 to-emerald-600 shadow-sm">
                        <Dumbbell className="w-4 h-4 text-white" />
                      </div>
                      <div className="flex-1 min-w-0">
                        <p className="text-sm font-medium text-gray-900 truncate">{session.exercise_title}</p>
                        <span className="text-xs text-gray-400">{new Date(session.date).toLocaleDateString()}</span>
                      </div>
                      {session.notes && (
                        <span className="text-xs text-gray-400 hidden sm:block truncate max-w-[200px]">{session.notes}</span>
                      )}
                    </div>
                  ))}
                </div>
              </div>
            )}
          </>
        )}

        {/* ─── VIDEOS TAB ─── */}
        {mainTab === "videos" && (
          <div className="text-center py-20">
            <Video className="w-16 h-16 mx-auto text-gray-300 dark:text-gray-600 mb-4" />
            <h3 className="text-xl font-bold text-gray-900 dark:text-white mb-2">Coming Soon...</h3>
            <p className="text-gray-500 dark:text-gray-400 text-sm max-w-md mx-auto">
              We&apos;re working on a curated video library with expert-led mental performance content. Stay tuned!
            </p>
          </div>
        )}

        {/* ─── AUDIO TAB ─── */}
        {mainTab === "audio" && (
          <AudioLibrary userRole={user?.role} />
        )}
      </div>

      {/* Exercise Dialog */}
      <ExerciseDialog
        open={showDialog}
        onOpenChange={setShowDialog}
        exercise={selectedExercise}
        previousSessions={previousExerciseSessions}
        exerciseNotes={exerciseNotes}
        setExerciseNotes={setExerciseNotes}
        isSpeaking={speech.isSpeaking}
        isPaused={speech.isPaused}
        isGenerating={speech.isGenerating}
        currentStep={speech.currentStep}
        speakInstructions={speech.speakInstructions}
        stopSpeech={speech.stopSpeech}
        togglePauseSpeech={speech.togglePauseSpeech}
        guidedMode={guided.guidedMode}
        guidedStep={guided.guidedStep}
        guidedTimer={guided.guidedTimer}
        guidedPaused={guided.guidedPaused}
        guidedComplete={guided.guidedComplete}
        startGuidedPractice={guided.startGuidedPractice}
        stopGuidedPractice={guided.stopGuidedPractice}
        toggleGuidedPause={guided.toggleGuidedPause}
        getStepDuration={guided.getStepDuration}
        onCompleteSession={handleCompleteSession}
      />

      {/* Notes Dialog */}
      {showNotesDialog && selectedExercise && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
          <div className="bg-white rounded-xl p-6 max-w-2xl w-full max-h-[90vh] overflow-y-auto">
            <div className="flex justify-between items-start mb-6">
              <div>
                <h2 className="text-xl font-bold text-gray-900 mb-1">Personal Notes</h2>
                <p className="text-sm text-gray-500">{selectedExercise.title}</p>
              </div>
              <button onClick={() => { setShowNotesDialog(false); setCurrentNotes(""); setSelectedExercise(null); }} className="text-gray-400 hover:text-gray-600">
                <X className="w-5 h-5" />
              </button>
            </div>
            <div className="mb-6">
              <label className="block text-sm font-semibold text-gray-700 mb-1.5">What resonated with you?</label>
              <textarea value={currentNotes} onChange={(e) => setCurrentNotes(e.target.value)} rows={8}
                className="w-full px-3.5 py-2.5 border border-gray-200 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-emerald-500/40 focus:border-emerald-400 resize-none"
                placeholder="Write your reflections, key takeaways, or things to remember..." />
            </div>
            <div className="flex gap-3">
              <button onClick={handleSaveNotes} className="flex-1 bg-emerald-600 hover:bg-emerald-700 text-white py-2.5 rounded-xl text-sm font-semibold transition-colors shadow-sm">
                Save Notes
              </button>
              {exerciseNotes[selectedExercise?.id] && (
                <button onClick={handleDeleteNotes} className="px-5 py-2.5 bg-red-600 text-white rounded-xl hover:bg-red-700 text-sm font-semibold transition-colors">Delete</button>
              )}
              <button onClick={() => { setShowNotesDialog(false); setCurrentNotes(""); setSelectedExercise(null); }}
                className="px-5 py-2.5 bg-gray-100 text-gray-700 rounded-xl hover:bg-gray-200 text-sm font-semibold transition-colors">Cancel</button>
            </div>
          </div>
        </div>
      )}

      {/* Video Player */}
      {showVideoPlayer && <VideoPlayerModal video={selectedVideo} onClose={() => { setShowVideoPlayer(false); setSelectedVideo(null); }} />}
    </div>
  );
}
